/**
 * Tenant warehouse list reads (WSRADM-0013 / WSRADM-0044).
 *
 * Bare `/admin/{users|sales|attendance|products}` paths so `resolveBaseUrl`
 * uses `apiBase()` (`/api`). Tenant is the JWT — never a slug on the path,
 * query, or header. Bearer only; no cookies, no CSRF.
 */
import { request } from '@/api/client';
import { ApiError } from '@/api/errors';
import {
  parseAttendancePage,
  parseProductSalesPage,
  parseProductsPage,
  parseSalesPage,
  parseUsersPage,
  parseWarehouseProduct,
  parseWarehouseUser,
} from './parse';
import type {
  AttendancePageResponse,
  PagedResponse,
  ProductSaleLine,
  WarehouseProduct,
  WarehouseSale,
  WarehouseUser,
} from './types';

export interface WarehouseListQuery {
  page: number;
  size: number;
  search: string;
  sort: string;
  order: 'asc' | 'desc';
}

/** `from` / `to` optional — omit both for an unbounded window (member/product tabs). */
export interface WarehouseDateListQuery extends WarehouseListQuery {
  from?: string;
  to?: string;
}

export interface WarehouseSalesQuery extends WarehouseDateListQuery {
  includeVoids: boolean;
  /** Exact `source_client_id` = user's `sourceUserId`. Omit when undefined. */
  memberId?: number;
}

export interface WarehouseAttendanceQuery extends WarehouseDateListQuery {
  memberId?: number;
  productId?: number;
}

export interface ProductSalesQuery extends WarehouseDateListQuery {
  sourceProductId: number;
}

/** All (`''`) omits the query key. */
export type ProductSourceFilter = '' | 'WSTUDIO' | 'LEGACY';

export interface ProductsListQuery extends WarehouseListQuery {
  includeRetired: boolean;
  unassigned: boolean;
  dimension: string;
  family: string;
  sourceSystem: ProductSourceFilter;
}

/** All (`''`) omits the query key. The API never defaults Active. */
export type MembershipStatusFilter =
  | ''
  | 'ACTIVE'
  | 'GRACE'
  | 'LOST'
  | 'NEVER_PURCHASED_MEMBERSHIP';

export type UsersSort = 'name' | 'email' | 'membershipStatus' | 'sourceUserId';
export type UsersOrder = 'asc' | 'desc';

export interface UsersListQuery {
  page: number;
  size: number;
  search: string;
  membershipStatus: MembershipStatusFilter;
  sort: UsersSort;
  order: UsersOrder;
}

const MEMBERSHIP_STATUS_VALUES: ReadonlySet<string> = new Set([
  'ACTIVE',
  'GRACE',
  'LOST',
  'NEVER_PURCHASED_MEMBERSHIP',
]);

function withSearch(
  base: Record<string, string | number>,
  search: string,
): Record<string, string | number> {
  const trimmed = search.trim();
  if (trimmed.length === 0) return base;
  return { ...base, search: trimmed };
}

function withSortOrder(
  params: Record<string, string | number>,
  sort: string,
  order: 'asc' | 'desc',
  defaultSort: string,
  defaultOrder: 'asc' | 'desc',
  keyOrderDefault: (key: string) => 'asc' | 'desc',
): void {
  if (sort !== defaultSort || order !== defaultOrder) params['sort'] = sort;
  if (order !== keyOrderDefault(sort)) params['order'] = order;
}

function withOptionalDateWindow(
  params: Record<string, string | number>,
  from: string | undefined,
  to: string | undefined,
): void {
  if (from !== undefined && from.trim().length > 0) params['from'] = from.trim();
  if (to !== undefined && to.trim().length > 0) params['to'] = to.trim();
}


function usersListParams(query: UsersListQuery): Record<string, string | number> {
  const params: Record<string, string | number> = { page: query.page, size: query.size };
  const trimmed = query.search.trim();
  if (trimmed.length >= 2) params['search'] = trimmed;
  if (MEMBERSHIP_STATUS_VALUES.has(query.membershipStatus)) {
    params['membershipStatus'] = query.membershipStatus;
  }
  // Name+asc is the API default — omit both. Name+desc must send sort=name
  // with order=desc so the allowlist key is explicit.
  if (query.sort !== 'name' || query.order !== 'asc') params['sort'] = query.sort;
  if (query.order !== 'asc') params['order'] = query.order;
  return params;
}

export function listUsers(query: UsersListQuery): Promise<PagedResponse<WarehouseUser>> {
  return request<unknown>('GET', '/admin/users', {
    query: usersListParams(query),
  }).then(parseUsersPage);
}

/** Single user by `sourceUserId` — same shape as a list row. */
export function getUser(sourceUserId: number): Promise<WarehouseUser> {
  return request<unknown>('GET', `/admin/users/${encodeURIComponent(String(sourceUserId))}`).then(
    (body) => {
      const user = parseWarehouseUser(body);
      if (user === null) throw new ApiError('UNKNOWN', 0, {});
      return user;
    },
  );
}

export function listSales(query: WarehouseSalesQuery): Promise<PagedResponse<WarehouseSale>> {
  const params: Record<string, string | number> = withSearch(
    { page: query.page, size: query.size },
    query.search,
  );
  withOptionalDateWindow(params, query.from, query.to);
  // Omit `deleted` when include-voids is on (API omit = voids included).
  // Default exclude-voids sends `deleted=false`. Never send `deleted=true`.
  if (!query.includeVoids) params['deleted'] = 'false';
  if (query.memberId !== undefined) params['memberId'] = query.memberId;
  withSortOrder(params, query.sort, query.order, 'saleDate', 'desc', (key) =>
    key === 'saleDate' ? 'desc' : 'asc',
  );
  return request<unknown>('GET', '/admin/sales', { query: params }).then(parseSalesPage);
}

export function listAttendance(
  query: WarehouseAttendanceQuery,
): Promise<AttendancePageResponse> {
  const params = withSearch({ page: query.page, size: query.size }, query.search);
  withOptionalDateWindow(params, query.from, query.to);
  if (query.memberId !== undefined) params['memberId'] = query.memberId;
  if (query.productId !== undefined) params['productId'] = query.productId;
  withSortOrder(params, query.sort, query.order, 'attendedOn', 'desc', (key) =>
    key === 'attendedOn' ? 'desc' : 'asc',
  );
  return request<unknown>('GET', '/admin/attendance', { query: params }).then(parseAttendancePage);
}

export function listProducts(query: ProductsListQuery): Promise<PagedResponse<WarehouseProduct>> {
  const params = withSearch({ page: query.page, size: query.size }, query.search);
  // Legacy rows are never current — sourceSystem=LEGACY only returns rows with includeRetired=true.
  if (query.includeRetired === true || query.sourceSystem === 'LEGACY') {
    params['includeRetired'] = 'true';
  }
  if (query.unassigned === true) params['unassigned'] = 'true';
  if (query.dimension !== '') params['dimension'] = query.dimension;
  // family without dimension is dropped; unassigned+family is a server contradiction (zero rows).
  if (query.dimension !== '' && query.family !== '' && query.unassigned !== true) {
    params['family'] = query.family;
  }
  if (query.sourceSystem !== '') params['sourceSystem'] = query.sourceSystem;
  withSortOrder(params, query.sort, query.order, 'name', 'asc', (key) =>
    key === 'sourceCreatedAt' ? 'desc' : 'asc',
  );
  return request<unknown>('GET', '/admin/products', { query: params }).then(parseProductsPage);
}

/** Single product by `sourceProductId` — same shape as a list row. Negative ids are real. */
export function getProduct(sourceProductId: number): Promise<WarehouseProduct> {
  return request<unknown>(
    'GET',
    `/admin/products/${encodeURIComponent(String(sourceProductId))}`,
  ).then((body) => {
    const product = parseWarehouseProduct(body);
    if (product === null) throw new ApiError('UNKNOWN', 0, {});
    return product;
  });
}

/** Paged sale lines for one product. `totalElements` counts lines — never sum `lineAmount`. */
export function listProductSales(
  query: ProductSalesQuery,
): Promise<PagedResponse<ProductSaleLine>> {
  const params = withSearch({ page: query.page, size: query.size }, query.search);
  withOptionalDateWindow(params, query.from, query.to);
  withSortOrder(params, query.sort, query.order, 'saleDate', 'desc', (key) =>
    key === 'saleDate' ? 'desc' : 'asc',
  );
  return request<unknown>(
    'GET',
    `/admin/products/${encodeURIComponent(String(query.sourceProductId))}/sales`,
    { query: params },
  ).then(parseProductSalesPage);
}
